import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { X, Plus, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { createUserSymptom } from '@/lib/db';
import type { HDType } from '@/lib/types';

interface CustomSymptomFormProps {
  onClose: () => void;
  onSymptomAdded: (symptomText: string) => void;
  energyType: HDType;
  userId: string;
}

export function CustomSymptomForm({ onClose, onSymptomAdded, energyType, userId }: CustomSymptomFormProps) {
  const [text, setText] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!text.trim()) return;

    try {
      setSubmitting(true);

      await createUserSymptom({
        userId,
        text,
        description,
        energyType,
        isPrivate: true
      });

      onSymptomAdded(text);

      toast({
        title: "Symptôme ajouté",
        description: "Votre ressenti a été enregistré avec succès.",
      });
    } catch (error) {
      console.error('Error adding custom symptom:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'ajouter le symptôme.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-4 bg-purple-50">
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">Décrivez votre ressenti</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ex: Tension inexpliquée dans le ventre"
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />

        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Décrivez plus en détail ce que vous ressentez..."
          className="min-h-[100px]"
        />

        <Button
          onClick={handleSubmit}
          disabled={!text.trim() || submitting}
          className="w-full flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Ajout en cours...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Ajouter ce symptôme
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}